// Device-local call history for the brain (iW6-D). The web/Android build keeps
// call metadata in synchronous localStorage (storage/callLog.ts) — deliberately
// NEVER on the server (privacy: the server must not be the keeper of who called
// whom for this app). Bare JSC has no localStorage, so the brain backs the same
// log with the native KV seam instead, exactly as favorites.ts mirrors
// storage/favorites.ts. The outcome-classification and `seen` rules are mirrored
// VERBATIM from storage/callLog.ts (the source of truth) — kept in sync by hand,
// because that module imports localStorage + serverCache and can't be imported
// into the WASM-free brain bundle.
//
// Entries are a DEVICE-LOCAL extension of the protocol `CallLogEntry`: they add a
// `peerName` snapshot resolved on-device at record time (mirrors the web's
// `StoredCallLogEntry` in storage/callLog.ts). This is NOT a wire type — the
// server's call log stays metadata-only (privacy: it never learns display-name↔
// call associations), so `peerName` is only ever stored/read on-device and never
// sent to or sourced from the server. Keeping the name means history stays human
// even after a friendship is severed (blocking severs it server-side, docs/02 §3,
// which used to turn every blocked peer's history into "Unknown caller").

import type { CallLogEntry, CallOutcome } from '@merkury/protocol';
import type { CallDirection, CallEndCause, EndedCallState } from '../call/types.js';
import type { KvStore } from '../storage/kv.js';

/**
 * Device-local call-log entry: the protocol `CallLogEntry` PLUS a `peerName`
 * snapshot. Extends the wire type locally rather than changing it (the shape is
 * never sent to the server). `peerName`, when present, is a non-empty display
 * name (mirrors storage/callLog.ts `StoredCallLogEntry`'s `z.string().min(1)`).
 */
export interface StoredCallLogEntry extends CallLogEntry {
  peerName?: string;
}

/** Resolve a peer's current display name (the brain's `nameCache`), or undefined
 *  when it isn't known yet. Injected so record + backfill share one source. */
export type PeerNameResolver = (userId: string) => string | undefined;

const CALL_LOG_KEY = 'callLog';
/** Keep only the most recent entries — a call log, not an archive (matches
 *  storage/callLog.ts CALL_LOG_CAP). */
const CALL_LOG_CAP = 200;

/**
 * Map the engine's end cause onto the log's outcome vocabulary — mirrors
 * storage/callLog.ts `outcomeFor` exactly. `connected` = ICE ever connected: a
 * hangup or lost connection after connecting is a completed call; before
 * connecting it never was. `answered_elsewhere` returns null (the device that
 * answered logs it; this one neither missed nor took it).
 */
function outcomeFor(
  cause: CallEndCause,
  direction: CallDirection,
  connected: boolean,
): CallOutcome | null {
  switch (cause) {
    case 'hangup':
      if (connected) return 'completed';
      // Never connected: an outgoing cancel reads "no answer" (missed, outgoing);
      // an incoming hangup while ringing is a decline.
      return direction === 'outgoing' ? 'missed' : 'declined';
    case 'connection_lost':
      return connected ? 'completed' : 'failed';
    case 'declined':
    case 'busy':
      return 'declined';
    case 'no_answer': // outgoing ring timed out — renders "no answer"
    case 'missed':
      return 'missed';
    case 'answered_elsewhere':
      return null;
    case 'failed':
    case 'mic_denied':
    case 'security':
      return 'failed';
  }
}

/**
 * The brain's device-local call log, KV-backed (same pattern as KvFavorites): an
 * in-memory newest-first snapshot written through to the native KV seam so a
 * cold reboot re-hydrates it. Never touches the network.
 */
export class KvCallLog {
  private cache: StoredCallLogEntry[] | undefined;

  constructor(
    private readonly kv: KvStore,
    private readonly resolveName?: PeerNameResolver,
  ) {}

  /** Hydrate the newest-first snapshot once from KV. */
  async load(): Promise<readonly StoredCallLogEntry[]> {
    if (this.cache === undefined) {
      const stored = await this.kv.get<StoredCallLogEntry[]>(CALL_LOG_KEY).catch(() => undefined);
      this.cache = Array.isArray(stored) ? stored : [];
    }
    return this.cache;
  }

  /** The log, newest first, capped at `limit` when one is given. Backfills any
   *  still-missing peer names on every read — mirrors storage/callLog.ts, so a
   *  name that became known after the row was recorded (contacts arrived, or an
   *  unblock re-friend) heals older history. */
  async list(limit?: number): Promise<readonly StoredCallLogEntry[]> {
    const all = await this.backfillNames(await this.load());
    return typeof limit === 'number' && limit >= 0 ? all.slice(0, limit) : all;
  }

  /**
   * Fill `peerName` on entries that lack one from the current resolver, and
   * persist through when anything changed — mirrors storage/callLog.ts
   * `backfillNames` (a blank/unknown name leaves the row untouched, keeping the
   * min-length-1 invariant). Referentially stable when nothing changes.
   */
  private async backfillNames(
    entries: readonly StoredCallLogEntry[],
  ): Promise<readonly StoredCallLogEntry[]> {
    if (!this.resolveName) return entries;
    let changed = false;
    const filled = entries.map((e) => {
      if (e.peerName) return e;
      const name = this.resolveName!(e.peerUserId);
      if (!name) return e;
      changed = true;
      return { ...e, peerName: name };
    });
    if (!changed) return entries;
    this.cache = filled;
    await this.kv.put(CALL_LOG_KEY, filled);
    return filled;
  }

  /**
   * Append the engine's ended call — mirrors storage/callLog.ts `recordEndedCall`.
   * Idempotent per callId (a double-fired subscriber must not duplicate history).
   * Returns the entry, or undefined when nothing is logged (answered elsewhere or
   * a duplicate). `seen` is false ONLY for an incoming missed call (the one that
   * badges); every other outcome is born seen (your own unanswered outgoing was
   * watched happening).
   */
  async record(ended: EndedCallState): Promise<StoredCallLogEntry | undefined> {
    const current = await this.load();
    if (current.some((e) => e.id === ended.callId)) return undefined;
    const connected = ended.connectedAt !== null;
    const outcome = outcomeFor(ended.cause, ended.direction, connected);
    if (outcome === null) return undefined;
    // Snapshot the peer's display name at record time (a non-empty name only,
    // per the min-length-1 rule) — mirrors storage/callLog.ts.
    const peerName = this.resolveName?.(ended.peerUserId);
    const entry: StoredCallLogEntry = {
      id: ended.callId,
      peerUserId: ended.peerUserId,
      ...(peerName ? { peerName } : {}),
      direction: ended.direction,
      // startedAt anchors the row timestamp AND the completed-call duration
      // (endedAt - startedAt): connect time when there is one, end time otherwise
      // (ring start isn't in EndedCallState — see storage/callLog.ts note).
      startedAt: new Date(connected ? (ended.connectedAt as number) : ended.endedAt).toISOString(),
      ...(outcome === 'completed' ? { endedAt: new Date(ended.endedAt).toISOString() } : {}),
      outcome,
      seen: !(outcome === 'missed' && ended.direction === 'incoming'),
    };
    this.cache = [entry, ...current].slice(0, CALL_LOG_CAP);
    await this.kv.put(CALL_LOG_KEY, this.cache);
    return entry;
  }

  /** Mark every missed call seen (clears the badge) — mirrors `markAllMissedSeen`.
   *  No-op (no write) when nothing is unseen. */
  async markAllMissedSeen(): Promise<void> {
    const current = await this.load();
    if (!current.some((e) => e.outcome === 'missed' && !e.seen)) return;
    this.cache = current.map((e) =>
      e.outcome === 'missed' && !e.seen ? { ...e, seen: true } : e,
    );
    await this.kv.put(CALL_LOG_KEY, this.cache);
  }

  /** Wipe — sign-out must not leave the previous account's history. */
  async clear(): Promise<void> {
    this.cache = [];
    await this.kv.delete(CALL_LOG_KEY).catch(() => undefined);
  }
}
